import { ApolloError, gql, useLazyQuery } from "@apollo/client";
import { Select } from "@src/common/components";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { SelectProps } from "baseui/select";

// End-customer lists can be large, so this picker searches server-side (by company,
// name, email, phone, or id) instead of preloading every customer like the small
// service/vehicle/package-type pickers do.

interface EndCustomerOption {
  id: string;
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  contactEmail?: string | null;
  displayId?: number | null;
}

const SEARCH_END_CUSTOMERS_FOR_PICKER = gql`
  query SearchEndCustomersForPicker($courierId: ID!, $searchTerm: String!) {
    searchEndCustomer(courierId: $courierId, searchTerm: $searchTerm) {
      id
      companyName
      firstName
      lastName
      contactEmail
      displayId
    }
  }
`;

// Resolves display labels for already-selected ids (edit mode starts with ids only).
const GET_END_CUSTOMERS_BY_IDS_FOR_PICKER = gql`
  query GetEndCustomersByIdsForPicker(
    $courierId: ID!
    $ids: [ID!]
    $limit: Int!
  ) {
    endCustomers(courierId: $courierId, ids: $ids, limit: $limit) {
      edges {
        node {
          id
          companyName
          firstName
          lastName
          contactEmail
          displayId
        }
      }
    }
  }
`;

function labelFor(option: EndCustomerOption): string {
  const name =
    option.companyName ||
    [option.firstName, option.lastName].filter(Boolean).join(" ") ||
    option.contactEmail ||
    option.id;
  return option.displayId != null ? `${option.displayId} · ${name}` : name;
}

interface EndCustomerMultiSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  hint?: string;
  size?: SelectProps["size"];
}

export function EndCustomerMultiSelect({
  value,
  onChange,
  disabled,
  hint,
  size,
}: EndCustomerMultiSelectProps) {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const [inputValue, setInputValue] = useState("");
  const debouncedInput = useDebounce<string>(inputValue, 400);
  // Accumulates id → label as it's learned from searches and id lookups, so
  // selected chips keep their names even after the search results change.
  const [labelsById, setLabelsById] = useState<Record<string, string>>({});

  const [search, { data: searchData, loading: searchLoading }] = useLazyQuery(
    SEARCH_END_CUSTOMERS_FOR_PICKER
  );
  const [fetchByIds] = useLazyQuery(GET_END_CUSTOMERS_BY_IDS_FOR_PICKER);

  const rememberLabels = (options: EndCustomerOption[]) => {
    if (options.length === 0) return;
    setLabelsById((prev) => {
      const next = { ...prev };
      options.forEach((option) => {
        next[option.id] = labelFor(option);
      });
      return next;
    });
  };

  useEffect(() => {
    if (!courierId || debouncedInput.trim().length === 0) return;
    search({ variables: { courierId, searchTerm: debouncedInput.trim() } }).catch(
      (error) => showError(error as ApolloError)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierId, debouncedInput, search]);

  useEffect(() => {
    const results: EndCustomerOption[] = searchData?.searchEndCustomer ?? [];
    rememberLabels(results);
  }, [searchData]);

  // Resolve labels for any selected ids we haven't seen yet (edit mode).
  useEffect(() => {
    if (!courierId) return;
    const missingIds = value.filter((id) => !labelsById[id]);
    if (missingIds.length === 0) return;
    fetchByIds({
      variables: { courierId, ids: missingIds, limit: missingIds.length },
    })
      .then((res) => {
        const nodes: EndCustomerOption[] = (
          res.data?.endCustomers?.edges ?? []
        )
          .map((edge: { node?: EndCustomerOption | null }) => edge?.node)
          .filter(
            (
              node: EndCustomerOption | null | undefined
            ): node is EndCustomerOption => Boolean(node)
          );
        rememberLabels(nodes);
      })
      .catch((error) => showError(error as ApolloError));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courierId, value]);

  // Once the box is cleared, the last search's results must not linger as
  // selectable options under an empty query -- only the dropdown's option
  // list is cleared here; labelsById (and therefore selected chips) is
  // untouched.
  const searchOptions = useMemo(() => {
    if (debouncedInput.trim().length === 0) return [];
    const results: EndCustomerOption[] = searchData?.searchEndCustomer ?? [];
    return results.map((option) => ({ id: option.id, label: labelFor(option) }));
  }, [searchData, debouncedInput]);

  const selectedOptions = value.map((id) => ({
    id,
    label: labelsById[id] ?? id,
  }));

  return (
    <div>
      <Select
        multi
        searchable
        clearable
        disabled={disabled}
        size={size}
        // While the debounce window hasn't settled yet, the options on
        // screen still answer the PREVIOUS term -- keep them visible
        // (avoids flicker on every keystroke) but mark them as pending.
        isLoading={searchLoading || inputValue.trim() !== debouncedInput.trim()}
        placeholder="Search customers…"
        options={searchOptions}
        value={selectedOptions}
        filterOptions={(options) => options}
        noResultsMsg={
          debouncedInput.trim().length === 0
            ? "Type to search customers"
            : "No matching customers"
        }
        onInputChange={(event) =>
          setInputValue((event.target as HTMLInputElement).value)
        }
        onChange={(params) => {
          rememberLabels(
            params.value.map((v) => ({
              id: v.id as string,
              companyName: v.label as string,
            }))
          );
          onChange(params.value.map((v) => v.id as string));
        }}
      />
      {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    </div>
  );
}
